import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Typography,
  Input,
  Space,
  App,
  Select,
  Button,
  Collapse,
} from 'antd';
import { Link } from 'react-router-dom';
import { Row } from 'antd';
import { resolveMedia } from '../../utils/media';
import {
  listGenders,
  listModalities,
  listPeriods,
} from '../../services/catalogs';
import '../../styles/evaluation.css';
import InfiniteScroll from 'react-infinite-scroll-component';
import { listEvaluations } from '../../services/evaluations';
import useAuth from '../../hooks/useAuth';
import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const EvaluationList = () => {
  const { message } = useApp();

  const { can } = usePermissions();

  const [sorter] = useState({});
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(null);
  const [genderFilter, setGenderFilter] = useState(null);
  const [draftSearch, setDraftSearch] = useState('');
  const [draftModality, setDraftModality] = useState(null);
  const [draftPeriod, setDraftPeriod] = useState(null);
  const [draftGender, setDraftGender] = useState(null);
  const [queryKey, setQueryKey] = useState(0);
  const [pending, setPending] = useState({
    items: [],
    page: 0,
    hasMore: true,
    total: 0,
  });

  const [evaluated, setEvaluated] = useState({
    items: [],
    page: 0,
    hasMore: true,
    total: 0,
  });
  const { user } = useAuth();

  // Catálogos para los filtros
  const [periods, setPeriods] = useState([]);
  const [genders, setGenders] = useState([]);
  const [modalities, setModalities] = useState([]);

  const resetAndReload = () => {
    setPending({
      items: [],
      page: 0,
      hasMore: true,
    });

    setEvaluated({
      items: [],
      page: 0,
      hasMore: true,
    });
  };

  const fetchData = useCallback(
    async (
      page,
      pageSize,
      sorter,
      search,
      modalityId,
      periodId,
      genderId,
      status,
      userId,
    ) => {
      const params = {
        search: search || undefined,
        page,
        limit: pageSize,
        sort: sorter?.field,
        order: sorter?.order,
        ...(modalityId && { modalityId }),
        ...(periodId && { periodId }),
        ...(genderId && { genderId }),
        ...(status && { status }),
        ...(userId && { userId }),
      };
      return await listEvaluations(params);
    },
    [],
  );

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [periodsData, gendersData, modalitiesData] = await Promise.all([
          listPeriods(),
          listGenders(),
          listModalities(),
        ]);
        setPeriods(periodsData || []);
        setGenders(gendersData || []);
        setModalities(modalitiesData || []);
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
        message.error('No se pudieron cargar los catálogos');
      }
    };
    loadCatalogs();
  }, [message]);

  const pendingRef = React.useRef(pending);
  const evaluatedRef = React.useRef(evaluated);

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(() => {
    evaluatedRef.current = evaluated;
  }, [evaluated]);

  const loadingRef = React.useRef({
    PENDING: false,
    EVALUATED: false,
  });

  const loadMore = useCallback(
    async (type) => {
      if (loadingRef.current[type]) return;

      const isPending = type === 'PENDING';
      const state = isPending ? pendingRef.current : evaluatedRef.current;
      const setState = isPending ? setPending : setEvaluated;

      if (!state.hasMore) return;

      loadingRef.current[type] = true;

      try {
        const { items, total } = await fetchData(
          state.page + 1,
          10,
          sorter,
          search,
          modalityFilter,
          periodFilter,
          genderFilter,
          type,
          ['Superadmin', 'admin', 'Administrador', 'administrador'].includes(
            user?.role,
          )
            ? undefined
            : user.id,
        );

        setState((prev) => {
          const existingIds = new Set(prev.items.map((v) => v.id));
          const filtered = items.filter((v) => !existingIds.has(v.id));

          const nextItems = [...prev.items, ...filtered];

          return {
            ...prev,
            items: nextItems,
            page: prev.page + 1,
            hasMore: nextItems.length < total,
            total,
          };
        });
      } finally {
        loadingRef.current[type] = false;
      }
    },
    [
      fetchData,
      sorter,
      search,
      modalityFilter,
      periodFilter,
      genderFilter,
      user,
    ],
  );

  useEffect(() => {
    loadMore('PENDING');
    loadMore('EVALUATED');
  }, [search, modalityFilter, periodFilter, genderFilter, queryKey, loadMore]);

  const onSearch = (value) => {
    setDraftSearch(value || '');
  };

  const handleModalityFilter = (value) => {
    setDraftModality(value);
  };

  const handlePeriodFilter = (value) => {
    setDraftPeriod(value);
  };

  const handleGenderFilter = (value) => {
    setDraftGender(value);
  };

  const handleSearch = () => {
    resetAndReload();
    setSearch(draftSearch);
    setModalityFilter(draftModality);
    setPeriodFilter(draftPeriod);
    setGenderFilter(draftGender);
    setQueryKey((prev) => prev + 1);
  };

  const handleClearFilters = () => {
    resetAndReload();
    // Limpiar draft
    setDraftSearch('');
    setDraftModality(null);
    setDraftPeriod(null);
    setDraftGender(null);

    // Limpiar aplicados
    setSearch('');
    setModalityFilter(null);
    setPeriodFilter(null);
    setGenderFilter(null);
    setQueryKey((prev) => prev + 1);
  };

  const VideoGrid = ({ videos }) => {
    const groupedVideos = videos.reduce((acc, video) => {
      const compId = video.competition?.id || 'sin_competencia';
      if (!acc[compId]) {
        acc[compId] = {
          competitionInfo: video.competition,
          videos: [],
        };
      }
      acc[compId].videos.push(video);
      return acc;
    }, {});

    const items = Object.keys(groupedVideos).map((compId) => {
      const group = groupedVideos[compId];
      const compInfo = group.competitionInfo;

      let headerLabel = 'Sin Competencia';
      if (compInfo) {
        const modalityStr = compInfo.modality?.description
          ? ` - ${compInfo.modality.description}`
          : '';
        headerLabel = `${compInfo.description || 'Competencia'}${modalityStr}`;
      }

      const pendingVideos = group.videos.filter(
        (video) => video.status === 'ACCEPTED',
      );
      const pendingCount = pendingVideos.length;

      return {
        key: compId,

        // 🔥 HEADER PERSONALIZADO
        label: (
          <div className="competition-header">
            <div>
              <strong>{headerLabel}</strong>
              <p className="pending">Videos pendientes: {pendingCount}</p>
            </div>
          </div>
        ),

        // 🔥 CONTENIDO (LISTA VERTICAL)
        children: (
          <div className="video-list">
            {group.videos.map((video) => (
              <div key={video.id} className="video-row">
                <div className="video-left">
                  <img
                    src={resolveMedia(video.urlThumbail)}
                    alt={video.folio}
                    className="thumbnail"
                  />

                  <div className="video-meta">
                    <p className="title">{video.title || 'Video sin título'}</p>
                    <p className="subtitle">{video.folio}</p>
                  </div>
                </div>

                <div className="video-actions">
                  {can('evaluation.evaluate') && (
                    <Link to={`/evaluations/${video.id}/rate`}>
                      <button className="btn-evaluate">Evaluar ahora ✓</button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        ),
      };
    });

    return (
      <Collapse
        items={items}
        defaultActiveKey={Object.keys(groupedVideos)}
        bordered={false}
        className="custom-collapse"
      />
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Competencias
        </Title>
      </div>
      <Card>
        {can('evaluation.view') && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Input.Search
                placeholder="Buscar"
                allowClear
                onSearch={onSearch}
                style={{ width: 200 }}
              />
              <Select
                placeholder="Modalidad"
                allowClear
                style={{ width: 150 }}
                value={draftModality}
                onChange={handleModalityFilter}
                options={modalities.map((modality) => ({
                  value: modality.id,
                  label: modality.description || modality.code,
                }))}
              />
              <Select
                placeholder="Periodo"
                allowClear
                style={{ width: 150 }}
                value={draftPeriod}
                onChange={handlePeriodFilter}
                options={periods.map((period) => ({
                  value: period.id,
                  label: period.description || period.code,
                }))}
              />
              <Select
                placeholder="Genero"
                allowClear
                style={{ width: 150 }}
                value={draftGender}
                onChange={handleGenderFilter}
                options={genders.map((gender) => ({
                  value: gender.id,
                  label: gender.description || gender.code,
                }))}
              />
              <Row justify="end" style={{ marginTop: 24 }}>
                <Space>
                  <Tooltip title="Buscar">
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                      style={{
                        backgroundColor: '#ff4d4f',
                        borderColor: '#ff4d4f',
                      }}
                      shape="circle"
                      size="large"
                    />
                  </Tooltip>
                  <Tooltip title="limpiar filtros">
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleClearFilters}
                      shape="circle"
                      size="large"
                    />
                  </Tooltip>
                </Space>
              </Row>
            </Space>
            <h2>Videos Pendientes por Evaluar Total {pending.total}</h2>
            <InfiniteScroll
              dataLength={pending.items.length}
              next={() => loadMore('PENDING')}
              hasMore={pending.hasMore}
            >
              <VideoGrid videos={pending.items} />
            </InfiniteScroll>

            <h2>Videos Calificados Total {evaluated.total}</h2>
            <InfiniteScroll
              dataLength={evaluated.items.length}
              next={() => loadMore('EVALUATED')}
              hasMore={evaluated.hasMore}
            >
              <VideoGrid videos={evaluated.items} />
            </InfiniteScroll>
          </Space>
        )}
      </Card>
    </div>
  );
};

export default EvaluationList;
